/**
 * ECharts chart upgrade utility for the Portal page.
 * Observes chart containers inside the widget's Shadow DOM
 * and replaces SVG charts with interactive ECharts instances.
 */
import * as echarts from 'echarts';

interface ChartDataset {
  label?: string;
  values: number[];
  color?: string;
}

interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'area';
  title?: string;
  data: {
    labels: string[];
    datasets: ChartDataset[];
  };
}

const PALETTE = [
  '#4f46e5', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#7c3aed', '#db2777', '#2563eb',
];

function specToEchartsOption(spec: ChartSpec): echarts.EChartsOption {
  const { type, title, data } = spec;

  if (type === 'pie') {
    const ds = data.datasets[0];
    return {
      title: title ? { text: title, left: 'center', textStyle: { fontSize: 13 } } : undefined,
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie',
        radius: '65%',
        center: ['50%', '55%'],
        data: ds.values.map((v, i) => ({
          name: data.labels[i] || `Item ${i + 1}`,
          value: v,
          itemStyle: { color: PALETTE[i % PALETTE.length] },
        })),
        label: { fontSize: 11 },
      }],
    };
  }

  // bar, line, area
  const series: echarts.SeriesOption[] = data.datasets.map((ds, i) => {
    const base: Record<string, unknown> = {
      name: ds.label || `Series ${i + 1}`,
      type: type === 'bar' ? 'bar' : 'line',
      data: ds.values,
      itemStyle: { color: ds.color || PALETTE[i % PALETTE.length] },
    };
    if (type === 'area') {
      base.areaStyle = { opacity: 0.2 };
    }
    return base as echarts.SeriesOption;
  });

  return {
    title: title ? { text: title, left: 'center', textStyle: { fontSize: 13 } } : undefined,
    tooltip: { trigger: 'axis' },
    legend: data.datasets.length > 1
      ? { bottom: 0, textStyle: { fontSize: 11 } }
      : undefined,
    grid: { left: 50, right: 20, top: title ? 40 : 20, bottom: data.datasets.length > 1 ? 40 : 30 },
    xAxis: {
      type: 'category',
      data: data.labels,
      axisLabel: { fontSize: 10, rotate: data.labels.length > 10 ? 30 : 0 },
    },
    yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
    series,
  };
}

/** Upgrade a single chart container from SVG to ECharts. */
function upgradeChart(container: HTMLElement): void {
  const raw = container.getAttribute('data-chart-spec');
  if (!raw) return;

  let spec: ChartSpec;
  try {
    spec = JSON.parse(raw);
  } catch {
    return; // Leave SVG as-is on parse error
  }

  if (!spec.type || !spec.data?.labels || !spec.data?.datasets?.length) return;

  // Create a div for ECharts (it needs explicit dimensions)
  const chartDiv = document.createElement('div');
  chartDiv.style.width = '100%';
  chartDiv.style.height = '260px';
  container.innerHTML = '';
  container.appendChild(chartDiv);

  const chart = echarts.init(chartDiv);
  chart.setOption(specToEchartsOption(spec));

  // Responsive resize
  const observer = new ResizeObserver(() => chart.resize());
  observer.observe(container);

  container.classList.add('chart-echarts');


  const mutObserver = new MutationObserver(() => {
    if (container.isConnected) return;
    chart.dispose();
    observer.disconnect();
    mutObserver.disconnect();
  });
  if (container.parentElement) {
    mutObserver.observe(container.parentElement, { childList: true });
  }
}

/**
 * Observe the widget's shadow DOM for chart containers and upgrade them to ECharts.
 * Returns a cleanup function to disconnect the observer.
 */
export function observeAndUpgradeCharts(shadowRoot: ShadowRoot): () => void {
  // Upgrade any already-rendered charts
  shadowRoot.querySelectorAll('.chart-container.chart-rendered:not(.chart-echarts)')
    .forEach(c => upgradeChart(c as HTMLElement));

  // Watch for new chart-rendered containers
  const observer = new MutationObserver(() => {
    shadowRoot.querySelectorAll('.chart-container.chart-rendered:not(.chart-echarts)')
      .forEach(c => upgradeChart(c as HTMLElement));
  });

  observer.observe(shadowRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  return () => observer.disconnect();
}
